'use client';

import React, { createContext, useContext, useEffect, useState, useTransition } from 'react';
import { setLocale as setLocaleAction } from './actions/locale';
import { defaultLocale, isValidLocale, type Locale } from '@/lib/i18n';

type Dictionary = typeof import('@/locales/it.json');

interface I18nContextType {
  locale: Locale;
  dictionary: Dictionary | null;
  setLocale: (locale: Locale) => void;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

export function useTranslations() {
  const { dictionary } = useI18n();
  if (!dictionary) {
    throw new Error('Dictionary not loaded');
  }
  return dictionary;
}

function getCookieLocale(): Locale | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  const localeCookie = cookies.find((cookie) => cookie.trim().startsWith('locale='));

  if (localeCookie) {
    const locale = localeCookie.split('=')[1]?.trim();
    if (locale && isValidLocale(locale)) {
      return locale;
    }
  }

  return null;
}

function getBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return defaultLocale;

  const browserLang = navigator.language?.toLowerCase().split('-')[0];
  if (browserLang && isValidLocale(browserLang)) {
    return browserLang;
  }

  return defaultLocale;
}

interface I18nProviderProps {
  children: React.ReactNode;
  initialLocale: Locale;
  initialDictionary: Dictionary;
}

export function I18nProvider({ children, initialLocale, initialDictionary }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [dictionary, setDictionary] = useState<Dictionary>(initialDictionary);
  const [isPending, startTransition] = useTransition();
  const [isInitialized, setIsInitialized] = useState(false);

  // Inizializza la lingua dal cookie o browser al mount
  useEffect(() => {
    if (isInitialized) return;

    const cookieLocale = getCookieLocale();
    const browserLocale = getBrowserLocale();
    const detectedLocale = cookieLocale || browserLocale;

    if (detectedLocale !== initialLocale) {
      startTransition(async () => {
        // Carica il dizionario per la lingua rilevata
        const dict = await import(`@/locales/${detectedLocale}.json`);
        setDictionary(dict.default);
        setLocaleState(detectedLocale);
        document.documentElement.lang = detectedLocale;

        // Salva nel cookie se non c'era già
        if (!cookieLocale) {
          await setLocaleAction(detectedLocale);
        }
      });
    }

    setIsInitialized(true);
  }, [initialLocale, isInitialized]);

  const setLocale = (newLocale: Locale) => {
    if (newLocale === locale) return;

    startTransition(async () => {
      await setLocaleAction(newLocale);
      setLocaleState(newLocale);

      // Carica il nuovo dizionario
      const dict = await import(`@/locales/${newLocale}.json`);
      setDictionary(dict.default);

      // Aggiorna l'attributo lang dell'html
      document.documentElement.lang = newLocale;
    });
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, dictionary, setLocale, isLoading: isPending }}>
      {children}
    </I18nContext.Provider>
  );
}
